import isVizio from '@accedo/xdk-device-vizio-smartcast-contrib/esm/detection'
import { Lightning, Router } from '@lightningjs/sdk'

import Button from '../components/button/button'
import { Divider } from '../components/divider/divider'
import { NativeInputBox } from '../components/nativeInputBox/nativeInputBox'
import PointerBackButton from '../components/NavigationPointers/PointerBackButton'
import { QwertyKeyboard } from '../components/QwertyKeyboard/QwertyKeyboard'
import { getColor, grey200, grey300, grey50, grey700, grey960, white } from '../constants/colors'
import { UniNeueHeavy, UniNeueRegular } from '../constants/fonts'
import { LOGIN_ICON_HEIGHT, LOGIN_ICON_WIDTH } from '../constants/general'
import { Images } from '../constants/paths'
import { LOGIN } from '../constants/routes'
import { AppConfig } from '../services/appConfig'
import { getText } from '../utils/dictionary'
import { getAsset, getIcon } from '../utils/image'
import { generateQR, validateEmail } from '../utils/misc'
import { captureScreenData, NON_BFF_SEGMENT_SCREENS } from '../utils/segmentAnalytics'
import PageHandler from './handlers/pageHandler'

enum ForgotPasswordStates {
  ResetLinkSentStep = 'ResetLinkSentStep',
  ForgotPasswordStep = 'ForgotPasswordStep',
  Keyboard = 'Keyboard',
}
export default class ForgotPassword extends PageHandler {
  private _currentButton: Lightning.Element
  private _sendLinkBtnEnabled: boolean
  private _email: string
  private _lastState: string

  static _template() {
    return {
      w: 1920,
      h: 1080,
      Container: {
        w: (w) => w,
        h: (h) => h,
        flex: {
          direction: 'column',
        },
        TopContainer: {
          h: 192,

          Logo: {
            w: 246,
            h: 28,
            y: 48,
            x: 48,
            texture: Lightning.Tools.getSvgTexture(getAsset(Images.LOGO_LARGE), 246, 28),
          },
          PointerBackButton: {
            type: PointerBackButton,
            x: (w) => w - 96,
            y: 68,
          },
        },
        ForgotPasswordContainer: {
          w: 1920,
          h: 888,
          rect: true,
          texture: Lightning.Tools.getSvgTexture(getAsset(Images.FORGOT_PASSWORD_BG), 1920, 888),
          flex: {
            direction: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
          },
          Title: {
            flexItem: {
              marginTop: 48,
            },
            w: 668,
            h: 58,
            text: {
              textAlign: 'center',
              text: getText('forgotPasswordTitle'),
              textColor: getColor(white),
              fontSize: 48,
              lineHeight: 48,
              letterSpacing: 0.48,
              fontFace: UniNeueHeavy,
            },
          },
          Description: {
            flexItem: {
              marginTop: 16,
            },
            w: 668,
            text: {
              textAlign: 'center',
              text: getText('forgotPasswordDescription'),
              textColor: getColor(grey200),
              fontSize: 28,
              lineHeight: 36,
              letterSpacing: 0.48,
              fontFace: UniNeueRegular,
            },
          },
          EmailInputField: {
            flexItem: {
              marginTop: 32,
            },
            type: NativeInputBox,
            w: 668,
            h: 84,
            placeholder: 'Email',
            fontSize: 32,
            textColor: getColor(grey50),
            placeholderColor: getColor(grey300),
            color: getColor(grey960),
            fontFace: UniNeueRegular,
            iconImage: Lightning.Tools.getSvgTexture(
              getIcon('icon-envelope'),
              LOGIN_ICON_WIDTH,
              LOGIN_ICON_HEIGHT,
            ),
            iconColor: getColor(grey50),
            onTextChangeSignal: 'onEmailChange',
            signals: {
              onEmailChange: 'onEmailChange',
            },
          },
          SendResetLinkButton: {
            flexItem: {
              marginTop: 32,
            },
            w: 668,
            h: 84,
            type: Button,
            label: getText('forgotPasswordSendLinkButton'),
            isEnabled: false,
          },
          Divider: {
            flexItem: {
              marginTop: 72,
            },
            type: Divider,
            w: 658,
            color: getColor(grey700),
          },
          QRCodeContainer: {
            flex: {
              direction: 'row',
              alignItems: 'center',
              justifyContent: 'center',
            },
            flexItem: {
              marginTop: 80,
            },
            QRCode: {
              w: 244,
              h: 244,
              rect: true,
              color: getColor(white),
              shader: null,
              src: '',
            },
            QrCodeDescription: {
              flexItem: {
                marginLeft: 32,
              },
              w: 382,
              h: 136,
              text: {
                text: getText('forgotPasswordQrCodeDescription'),
                textColor: getColor(grey200),
                fontSize: 24,
                lineHeight: 34,
                textAlign: 'left',
                fontFace: UniNeueRegular,
                wordWrapWidth: 382,
                maxLines: 4,
                advancedRenderer: true,
              },
            },
          },
        },
        ResetLinkSentContainer: {
          visible: false,
          w: 1920,
          h: 888,
          rect: true,
          texture: Lightning.Tools.getSvgTexture(getAsset(Images.FORGOT_PASSWORD_BG), 1920, 888),
          flex: {
            direction: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
          },
          Title2: {
            flexItem: {
              marginTop: 113,
            },
            w: 668,
            text: {
              textAlign: 'center',
              text: getText('forgotPasswordResetLinkTitle'),
              textColor: getColor(white),
              fontSize: 48,
              lineHeight: 56,
              letterSpacing: 0.48,
              fontFace: UniNeueHeavy,
            },
          },
          Description2: {
            flexItem: {
              marginTop: 24,
            },
            w: 668,
            text: {
              textAlign: 'center',
              text: getText('forgotPasswordResetLinkDescription'),
              textColor: getColor(grey200),
              fontSize: 28,
              lineHeight: 36,
              letterSpacing: 0.48,
              fontFace: UniNeueRegular,
            },
          },
          LoginButton: {
            flexItem: {
              marginTop: 32,
            },
            w: 668,
            h: 84,
            type: Button,
            label: getText('loginButton'),
          },
          ExitButton: {
            flexItem: {
              marginTop: 24,
            },
            w: 668,
            h: 84,
            type: Button,
            label: getText('exit'),
          },
        },
      },
      Keyboard: {
        x: 540,
        y: 615,
        visible: isVizio,
        type: QwertyKeyboard,
      },
    }
  }

  get keyboard() {
    return this.tag('Keyboard')
  }

  get forgotPasswordContainer() {
    return this.tag('ForgotPasswordContainer')
  }

  get resetLinkSentContainer() {
    return this.tag('ResetLinkSentContainer')
  }

  get emailInputField() {
    return this.tag('EmailInputField') as NativeInputBox
  }

  get buttonSendLink() {
    return this.tag('SendResetLinkButton') as Button
  }

  get buttonLogin() {
    return this.tag('LoginButton') as Button
  }

  get buttonExit() {
    return this.tag('ExitButton') as Button
  }

  get pointerBackButton() {
    return this.tag('PointerBackButton') as PointerBackButton
  }

  get qrCode() {
    return this.tag('QRCode')
  }

  set params(v: { email: string }) {
    this.emailInputField.setText(v?.email)
    this.onEmailChange(v?.email)

    requestAnimationFrame(() => {
      this.emailInputField.resetCursor()
    })
  }

  static _states() {
    return [
      class Keyboard extends this {
        _getFocused() {
          return this.keyboard
        }
      },
      class ForgotPasswordStep extends this {
        _getFocused() {
          return this._currentButton
        }

        $enter() {
          this.showForgotPasswordContainer()
          this._currentButton = this.emailInputField
        }

        async _handleEnter() {
          if (this._currentButton === this.emailInputField) {
            this.emailInputField.startInput()
          } else if (this._currentButton === this.buttonSendLink) {
            this.setLoadingAnimation(true)
            const success = await this.sendForgotPassword()
            this.setLoadingAnimation(false)
            if (success) {
              this._setState(ForgotPasswordStates.ResetLinkSentStep)
            } else {
              this.setModal(true, {
                wordWrap: true,
                buttons: [
                  {
                    label: getText('tryAgainButton'),
                    callback: () => {
                      this.setModal(false)
                      Router.focusPage()
                    },
                  },
                ],
              })
              Router.focusWidget('Modal')
            }
          }
        }

        _handleUp() {
          if (this._currentButton === this.buttonSendLink) {
            this._currentButton = this.emailInputField
          }
        }

        _handleDown() {
          if (this._currentButton === this.emailInputField && this._sendLinkBtnEnabled) {
            this._currentButton = this.buttonSendLink
          }
        }
      },
      class ResetLinkSentStep extends this {
        _getFocused() {
          return this._currentButton
        }

        $enter() {
          this.showResetLinkSentContainer()
          this._currentButton = this.buttonLogin
        }

        _handleEnter() {
          if (this._currentButton === this.buttonLogin) {
            Router.navigate(LOGIN, { email: this._email })
          } else if (this._currentButton === this.buttonExit) {
            Router.go(-2)
          }
        }

        _handleDown() {
          if (this._currentButton === this.buttonLogin) {
            this._currentButton = this.buttonExit
          }
        }

        _handleUp() {
          if (this._currentButton === this.buttonExit) {
            this._currentButton = this.buttonLogin
          }
        }
      },
    ]
  }

  _init() {
    this._setState(ForgotPasswordStates.ForgotPasswordStep)

    const { isCursorVisible } = AppConfig.shared.appSettings
    this.pointerBackButton.visible = isCursorVisible
  }

  async _active() {
    this.setLoadingAnimation(true)

    await this.setQR(getText('loginForgotPasswordUrl'))
    void captureScreenData(NON_BFF_SEGMENT_SCREENS.ResetPassword)

    this.setLoadingAnimation(false)
  }

  _inactive() {
    this.emailInputField.closeInput()
  }

  async setQR(textToQr: string) {
    this.qrCode.patch({ src: await generateQR(textToQr) })
  }

  _getFocused() {
    return this._currentButton
  }

  _handleBack() {
    Router.back()
  }

  onEmailChange(value) {
    this._sendLinkBtnEnabled = validateEmail(value)
    this.buttonSendLink.isEnabled = this._sendLinkBtnEnabled
  }

  showForgotPasswordContainer() {
    this.forgotPasswordContainer.visible = true
    this.resetLinkSentContainer.visible = false
  }

  showResetLinkSentContainer() {
    this.forgotPasswordContainer.visible = false
    this.resetLinkSentContainer.visible = true
  }

  sendForgotPassword() {
    this._email = this.emailInputField.getText()
    return AppConfig.shared.authService.sendForgotPasswordRequest({ email: this._email })
  }

  $updateFocusByHover(element) {
    this._currentButton = element
    this._refocus()
  }

  _handleClick(element) {
    if (element === this.buttonSendLink) {
      this.emailInputField.closeInput()
    }

    if (element === this.emailInputField || element === this.buttonSendLink) {
      this._setState(ForgotPasswordStates.ForgotPasswordStep)
      this._currentButton = element

      if (this._currentButton === this.emailInputField) {
        this.emailInputField.resetCursor()
      }

      ;(this as any)._handleEnter()
    } else if (element === this.buttonLogin || element === this.buttonExit) {
      this._setState(ForgotPasswordStates.ResetLinkSentStep)
      this._currentButton = element
      ;(this as any)._handleEnter()
    }
  }

  _onCursorEnter() {
    const { isCursorVisible } = AppConfig.shared.appSettings
    this.pointerBackButton.visible = isCursorVisible
  }

  _onCursorLeave() {
    ;(this._currentButton as any)?._focus()
    this.pointerBackButton.visible = false
  }

  validateEmailString() {
    let curText = this.emailInputField.getText()
    this._sendLinkBtnEnabled = validateEmail(curText)
    this.buttonSendLink.isEnabled = this._sendLinkBtnEnabled
  }

  $focusVizioKeyboard() {
    this._lastState = this._getState()
    this._setState(ForgotPasswordStates.Keyboard)
  }

  $unfocusVizioKeyboard() {
    this.validateEmailString()
    this._setState(this._lastState)
  }

  $onVizioKeyboardKeyPress(key: string) {
    let curText = this.emailInputField.getText()

    switch (key) {
      case 'Done':
        this.validateEmailString()
        this._setState(this._lastState)
        return

      case 'Clear':
        this.emailInputField.setText('')
        return

      case 'Backspace':
        if (curText.length > 0) {
          this.emailInputField.setText(curText.slice(0, -1))
        } else {
          this.$unfocusVizioKeyboard()
        }
        return

      case 'Space':
        this.emailInputField.setText(curText + ' ')
        return

      default:
        this.emailInputField.setText(curText === '' ? key : curText + key)
    }
  }
}
